import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Loader2, Layers } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { DashboardLayout } from '@/components/layout';
import { Button, Modal, ModalFooter } from '@/components/ui';
import { CreateLinkPageModal, LinkPageCard } from '@/components/linkpage';
import { useLinkPages } from '@/hooks/useLinkPages';
import { getLinkPageViews } from '@/services/linkpages';
import { useToast } from '@/components/ui';

const LinkPages = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { linkPages, loading, error, create, remove, toggleStatus } = useLinkPages();
  const { addToast } = useToast();

  const [viewsMap, setViewsMap] = useState<Record<string, number>>({});
  const [viewsLoading, setViewsLoading] = useState(false);

  useEffect(() => {
    if (linkPages.length === 0) return;
    setViewsLoading(true);
    Promise.all(
      linkPages.map(async (page) => {
        const count = await getLinkPageViews(page.id);
        return [page.id, count] as const;
      })
    )
      .then((results) => {
        const map: Record<string, number> = {};
        for (const [id, count] of results) {
          map[id] = count;
        }
        setViewsMap(map);
      })
      .catch((err) => console.error('Failed to load view counts:', err))
      .finally(() => setViewsLoading(false));
  }, [linkPages]);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const handleCreate = async (data: { title: string; description?: string }) => {
    const newPage = await create(data);
    addToast({
      type: 'success',
      title: t('linkPages.created'),
      message: t('linkPages.createdMessage'),
    });
    navigate(`/linkpages/${newPage.id}`);
  };

  const handleDelete = async (id: string) => {
    try {
      await remove(id);
      setDeleteConfirm(null);
      addToast({
        type: 'success',
        title: t('linkPages.deleted'),
        message: t('linkPages.deletedMessage'),
      });
    } catch (err) {
      addToast({
        type: 'error',
        title: t('linkPages.failedToDelete'),
        message: err instanceof Error ? err.message : t('common.somethingWentWrong'),
      });
    }
  };

  const handleToggleStatus = async (id: string) => {
    try {
      const updated = await toggleStatus(id);
      const isDisabled = updated.status === 'disabled';
      addToast({
        type: isDisabled ? 'warning' : 'success',
        title: isDisabled ? t('linkPages.disabled') : t('linkPages.enabled'),
        message: isDisabled ? t('linkPages.disabledMessage') : t('linkPages.enabledMessage'),
      });
    } catch (err) {
      addToast({
        type: 'error',
        title: t('common.somethingWentWrong'),
        message: err instanceof Error ? err.message : '',
      });
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-lime-500" />
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="text-center py-16">
          <p className="text-red-500 mb-2 font-medium">{t('common.somethingWentWrong')}</p>
          <p className="text-gray-400 text-sm">{error}</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-dark-800 dark:text-white mb-2">
            {t('linkPages.title')}
          </h1>
          <p className="text-gray-500 dark:text-gray-400">
            {linkPages.length === 0
              ? t('linkPages.emptyDescription')
              : t('linkPages.count', { count: linkPages.length })}
          </p>
        </div>
        <Button
          variant="primary"
          size="md"
          leftIcon={<Plus className="w-5 h-5" />}
          onClick={() => setIsCreateModalOpen(true)}
          className="flex-shrink-0"
        >
          {t('linkPages.createLinkPage')}
        </Button>
      </div>

      {/* Link Pages List */}
      {linkPages.length === 0 ? (
        <div className="text-center py-16">
          <Layers className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-500 dark:text-gray-400 mb-2">
            {t('linkPages.emptyDescription')}
          </h3>
          <Button
            variant="primary"
            size="md"
            leftIcon={<Plus className="w-5 h-5" />}
            onClick={() => setIsCreateModalOpen(true)}
          >
            {t('linkPages.createLinkPage')}
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {linkPages.map((page) => (
            <LinkPageCard
              key={page.id}
              linkPage={page}
              views={viewsMap[page.id]}
              viewsLoading={viewsLoading}
              onDelete={(id) => setDeleteConfirm(id)}
              onToggleStatus={handleToggleStatus}
            />
          ))}
        </div>
      )}

      {/* Create Modal */}
      <CreateLinkPageModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreate}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title={t('linkPages.deleteLinkPage')}
        description={t('linkPages.deleteConfirmation')}
        size="sm"
      >
        <ModalFooter>
          <Button variant="ghost" onClick={() => setDeleteConfirm(null)}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            className="bg-red-500 hover:bg-red-600"
            onClick={() => deleteConfirm && handleDelete(deleteConfirm)}
          >
            {t('common.delete')}
          </Button>
        </ModalFooter>
      </Modal>
    </DashboardLayout>
  );
};

export default LinkPages;
